import { useMemo, useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, Pagination, useTheme
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Daily movement behind the growth chart: how many clients came in, how many stopped
// paying, and what that did to the monthly maintenance. Each row opens into the
// clients themselves, the same way the other financial tabs do.

const PAGE_SIZE = 15;        // days per page
const DETAIL_PAGE_SIZE = 8;  // clients per page inside an open day

// Shades with enough text contrast in both themes (plain green/red do not pass)
const VALUE_COLORS = {
  light: { positive: '#157a55', negative: '#b3261e', suspended: '#9a6800' },
  dark: { positive: '#4ade80', negative: '#ff8a80', suspended: '#e0a516' },
};

const formatMoney = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatSignedMoney = (value) => `${value > 0 ? '+' : ''}${formatMoney(value)}`;
const formatSignedCount = (value) => `${value > 0 ? '+' : ''}${value}`;

// dd/mm/yyyy from a YYYY-MM-DD key — no Date involved, so no timezone shifting
const formatDay = (date) => {
  const [year, month, day] = (date || '').split('-');
  return year ? `${day}/${month}/${year}` : date;
};

// Bottom aligned so the two number lines of every cell sit exactly on the
// "clientes:" / "manutenção:" labels of the first column
const NUMBER_CELL = { whiteSpace: 'nowrap', py: 0.75, width: 130, verticalAlign: 'bottom' };
const GROUP_BORDER = { borderLeft: '1px solid', borderLeftColor: 'divider' };

// The two readings of a row, same size, split by a dashed rule that runs across
// every column — the top line is always clients, the bottom one always money
const LINE_ONE = { fontSize: 13, lineHeight: 1.5 };
const LINE_TWO = {
  fontSize: 13,
  lineHeight: 1.5,
  borderTop: '1px dashed',
  borderTopColor: 'divider',
};

// Names the two readings of the row, lined up with the numbers on the right
const RowLabels = () => (
  <Box>
    <Typography sx={{ ...LINE_ONE, color: 'text.disabled' }}>Clientes</Typography>
    <Typography sx={{ ...LINE_TWO, color: 'text.disabled' }}>Manutenção</Typography>
  </Box>
);

// Every cell carries both readings: how many clients on top, how much monthly
// maintenance below. `variant` decides the sign and who colours what:
//   in   - an entry, drawn as it is
//   out  - a loss, drawn negative so the row adds up to the result
//   net  - a balance, where each line takes the colour of its own sign
const MovementCell = ({ count, maintenance, color, muted, valueColor, variant = 'in', sx }) => {
  const cellSx = { ...NUMBER_CELL, ...sx };

  // A day with no movement of this kind: a dash reads faster than two zeros. The
  // hidden second line keeps every cell two lines tall, so the rows stay aligned.
  if (!count && !maintenance) {
    return (
      <TableCell align="right" sx={cellSx}>
        <Typography sx={{ ...LINE_ONE, color: muted }}>—</Typography>
        <Typography sx={{ ...LINE_TWO, color: muted }}>—</Typography>
      </TableCell>
    );
  }

  const countValue = variant === 'out' ? -Math.abs(count) : count;
  const moneyValue = variant === 'out' ? -Math.abs(maintenance) : maintenance;
  const signedText = variant !== 'in';

  return (
    <TableCell align="right" sx={cellSx}>
      <Typography
        sx={{
          ...LINE_ONE,
          fontWeight: 600,
          color: variant === 'net' ? valueColor(countValue) : color,
        }}
      >
        {signedText ? formatSignedCount(countValue) : countValue}
      </Typography>
      <Typography
        sx={{
          ...LINE_TWO,
          fontWeight: 600,
          color: variant === 'net' ? valueColor(moneyValue) : color,
        }}
      >
        {signedText ? formatSignedMoney(moneyValue) : formatMoney(moneyValue)}
      </Typography>
    </TableCell>
  );
};

// The clients of one movement type on an open day
const DetailSection = ({ title, items, color }) => {
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [items]);

  const pageCount = Math.max(1, Math.ceil(items.length / DETAIL_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * DETAIL_PAGE_SIZE;
  const visible = items.slice(offset, offset + DETAIL_PAGE_SIZE);

  const row = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 1,
    px: 1,
    py: 0.4,
    borderBottom: '1px solid',
    borderColor: 'divider',
  };

  return (
    <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
      <Box sx={{ ...row, py: 0.5 }}>
        <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 700, color, letterSpacing: 0.3 }}>
          {title.toUpperCase()}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{items.length}</Typography>
      </Box>

      {!items.length ? (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', px: 1, py: 0.5 }}>Nenhum</Typography>
      ) : (
        visible.map((item) => (
          <Box key={item.id} sx={row}>
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12 }} noWrap title={item.clientName}>
              {item.clientName}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color, whiteSpace: 'nowrap' }}>
              {formatMoney(item.value)}
            </Typography>
          </Box>
        ))
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={currentPage}
            onChange={(_, p) => setPage(p)}
            siblingCount={0}
            boundaryCount={1}
          />
        </Box>
      )}
    </Box>
  );
};

export default function GrowthTimeline({ days = [], totals, footnote }) {
  const theme = useTheme();
  const colors = VALUE_COLORS[theme.palette.mode === 'dark' ? 'dark' : 'light'];
  const muted = theme.palette.text.disabled;

  const [page, setPage] = useState(1);
  const [openDay, setOpenDay] = useState(null);

  useEffect(() => { setPage(1); setOpenDay(null); }, [days]);

  const pageCount = Math.max(1, Math.ceil(days.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = useMemo(
    () => days.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [days, currentPage]
  );

  const valueColor = (value) => (value === 0 ? muted : value > 0 ? colors.positive : colors.negative);

  const headCell = {
    fontSize: 12,
    fontWeight: 600,
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
  };

  if (!days.length) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
        Sem movimentação no período
      </Typography>
    );
  }

  // Same cells for a day and for the total line
  const movementCells = (row, extra = {}) => [
    <MovementCell
      key="newClients"
      count={row.newClients.count}
      maintenance={row.newClients.maintenance}
      color={colors.positive}
      muted={muted}
      valueColor={valueColor}
      sx={extra}
    />,
    <MovementCell
      key="suspended"
      count={row.suspended.count}
      maintenance={row.suspended.maintenance}
      color={colors.suspended}
      muted={muted}
      valueColor={valueColor}
      variant="out"
      sx={extra}
    />,
    <MovementCell
      key="canceled"
      count={row.canceled.count}
      maintenance={row.canceled.maintenance}
      color={colors.negative}
      muted={muted}
      valueColor={valueColor}
      variant="out"
      sx={extra}
    />,
    <MovementCell
      key="result"
      count={row.result.count}
      maintenance={row.result.maintenance}
      muted={muted}
      valueColor={valueColor}
      variant="net"
      sx={{ ...GROUP_BORDER, ...extra }}
    />,
  ];

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Movimentação por dia
      </Typography>

      {/* Narrow on purpose: spread over the full width the two readings of each cell
          stop reading as a pair */}
      <TableContainer sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCell}>Data</TableCell>
              <TableCell align="right" sx={{ ...headCell, ...NUMBER_CELL }}>Clientes novos</TableCell>
              <TableCell align="right" sx={{ ...headCell, ...NUMBER_CELL }}>Suspensos</TableCell>
              <TableCell align="right" sx={{ ...headCell, ...NUMBER_CELL }}>Cancelados</TableCell>
              <TableCell align="right" sx={{ ...headCell, ...NUMBER_CELL, ...GROUP_BORDER }}>Resultado</TableCell>
              <TableCell align="right" sx={{ ...headCell, ...NUMBER_CELL }}>Acumulado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((day) => {
              const isOpen = openDay === day.date;
              return [
                <TableRow
                  key={day.date}
                  hover
                  onClick={() => setOpenDay(isOpen ? null : day.date)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.75, verticalAlign: 'bottom' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <ChevronRightIcon
                        sx={{
                          fontSize: 18,
                          color: 'text.secondary',
                          transform: isOpen ? 'rotate(90deg)' : 'none',
                          transition: 'transform 160ms',
                        }}
                      />
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDay(day.date)}</Typography>
                    </Box>
                    <Box sx={{ pl: 3 }}><RowLabels /></Box>
                  </TableCell>
                  {movementCells(day)}
                  <MovementCell
                    count={day.running.count}
                    maintenance={day.running.maintenance}
                    muted={muted}
                    valueColor={valueColor}
                    variant="net"
                  />
                </TableRow>,
                <TableRow key={`${day.date}-detail`}>
                  <TableCell colSpan={6} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                    <Collapse in={isOpen} unmountOnExit>
                      {/* Slightly distinct background so the breakdown reads as nested */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          pl: 4,
                          pr: 1,
                          py: 0.5,
                          bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)'),
                        }}
                      >
                        <DetailSection title="Clientes novos" items={day.newClients.items || []} color={colors.positive} />
                        <DetailSection title="Suspensões" items={day.suspended.items || []} color={colors.suspended} />
                        <DetailSection title="Cancelamentos" items={day.canceled.items || []} color={colors.negative} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>,
              ];
            })}

            {!!totals && (
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap', py: 0.75, verticalAlign: 'bottom' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.25 }}>Total do período</Typography>
                  <Box sx={{ pl: 3 }}><RowLabels /></Box>
                </TableCell>
                {movementCells(totals, { fontWeight: 700 })}
                <TableCell sx={NUMBER_CELL} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1, pt: 1, maxWidth: 1000, mx: 'auto',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {days.length} {days.length === 1 ? 'dia com movimento' : 'dias com movimento'}
        </Typography>
        {pageCount > 1 && (
          <Pagination
            size="small"
            count={pageCount}
            page={currentPage}
            onChange={(_, p) => { setPage(p); setOpenDay(null); }}
            siblingCount={0}
            boundaryCount={1}
          />
        )}
      </Box>

      {footnote && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1.5, maxWidth: 1000, mx: 'auto' }}
        >
          {footnote}
        </Typography>
      )}
    </Box>
  );
}
